import { Node, mergeAttributes, type JSONContent } from '@tiptap/core'
import { NodeViewContent, NodeViewWrapper, ReactNodeViewRenderer, type NodeViewProps } from '@tiptap/react'
import { QUOTE_HEADER_CLASS, QuoteHeaderContent } from './quote-header'

// Labeled header the model emits on its own line before a verbatim blockquote
// (`<quote_header>Email to Sarah</quote_header>`), rendered as a label plus a
// copy button for the quote's text. The pairing is positional, not structural:
// the copy target is whatever blockquote directly follows the header, resolved
// at click time — deleting the header just leaves a plain blockquote, and
// clicking copy on a header with no quote after it is a no-op.
const TAG = 'quote_header'

const readLabel = (node: { attrs?: Record<string, unknown> | null }): string =>
  typeof node.attrs?.label === 'string' ? node.attrs.label : ''

/** Markdown of the next blockquote after the header, without the `> ` prefixes.
 *
 * The header may be parsed inline inside a paragraph (marked.js treats unknown
 * HTML tags as inline) rather than as a top-level block. We walk UP to whichever
 * depth-1 ancestor contains the header, then scan forward at the doc level past
 * any empty paragraphs (from the blank line between the tag and the quote).
 */
function quoteMarkdownAfter(editor: NodeViewProps['editor'], headerPos: number): string | null {
  const doc = editor.state.doc
  const $header = doc.resolve(headerPos)

  let pos = $header.depth === 0 ? headerPos + ($header.nodeAfter?.nodeSize ?? 0) : $header.after(1)

  while (pos < doc.nodeSize - 1) {
    const sibling = doc.resolve(pos).nodeAfter
    if (!sibling) break
    if (sibling.type.name === 'blockquote') {
      const content = (sibling.toJSON() as JSONContent).content
      if (content?.length) {
        // Serialize the children (not the blockquote itself) to drop `> ` prefixes.
        const markdown = editor.markdown?.serialize({ type: 'doc', content })
        const text = (markdown ?? sibling.textContent).trim()
        if (text) return text
      }
      // Empty / blank blockquote: keep scanning for a real quote after it.
      pos += sibling.nodeSize
      continue
    }
    // Skip empty paragraphs; stop at anything else.
    if (sibling.type.name !== 'paragraph' || sibling.textContent.trim() !== '') break
    pos += sibling.nodeSize
  }
  return null
}

function QuoteHeaderNodeView({ node, editor, getPos }: NodeViewProps) {
  const label = readLabel(node)

  const resolveQuote = () => {
    const at = getPos()
    if (typeof at !== 'number') {
      console.warn('Copy quote: header position unresolved, nothing copied.')
      return null
    }
    return quoteMarkdownAfter(editor, at)
  }

  return (
    <NodeViewWrapper as="div" contentEditable={false} className={QUOTE_HEADER_CLASS}>
      <QuoteHeaderContent label={label} resolveQuote={resolveQuote} />
    </NodeViewWrapper>
  )
}

export const QuoteHeader = Node.create({
  name: 'quoteHeader',
  group: 'block',
  atom: true,

  addAttributes() {
    return {
      label: {
        default: '',
        parseHTML: (el) => (el.textContent ?? '').trim(),
        renderHTML: () => ({}),
      },
    }
  },

  parseHTML() {
    return [{ tag: TAG }]
  },

  markdownTokenizer: {
    name: 'quoteHeader',
    level: 'block',
    start: (source) => source.search(/^ {0,3}<quote_header>/m),
    tokenize(source) {
      const match = /^ {0,3}<quote_header>[\s\S]*?<\/quote_header>[\t ]*(?:\r?\n|$)/.exec(source)
      if (!match) {
        return undefined
      }
      // Inline HTML parsing discards this block atom, including its copy button.
      return { type: 'html', raw: match[0], text: match[0], block: true }
    },
  },

  renderHTML({ node, HTMLAttributes }) {
    return [TAG, mergeAttributes(HTMLAttributes), readLabel(node)]
  },

  renderMarkdown(node) {
    return `\n<${TAG}>${readLabel(node)}</${TAG}>\n`
  },

  addNodeView() {
    return ReactNodeViewRenderer(QuoteHeaderNodeView)
  },
})

// Replaces the StarterKit blockquote with a callout-box node view.
// Declared via Node.create (not Blockquote.extend) to avoid a direct dependency
// on @tiptap/extension-blockquote, which isn't in our package.json. The name
// 'blockquote' matches StarterKit's node; buildExtensions' last-write-wins dedup
// swaps it in cleanly. parseMarkdown/renderMarkdown mirror the stock Blockquote
// so getMarkdown() still emits `>` blockquotes after edits.
export const CalloutBlockquote = Node.create({
  name: 'blockquote',
  group: 'block',
  content: 'block+',
  defining: true,

  parseHTML() {
    return [{ tag: 'blockquote' }]
  },

  renderHTML({ HTMLAttributes }) {
    return ['blockquote', mergeAttributes(HTMLAttributes), 0]
  },

  parseMarkdown: (token, helpers) => {
    const parseBlockChildren = helpers.parseBlockChildren ?? helpers.parseChildren
    return helpers.createNode('blockquote', undefined, parseBlockChildren(token.tokens || []))
  },

  renderMarkdown: (node, h) => {
    if (!node.content) return ''
    const prefix = '>'
    const result: Array<string> = []
    node.content.forEach((child, index) => {
      const childContent = h.renderChild?.(child, index) ?? h.renderChildren([child])
      const linesWithPrefix = childContent
        .split('\n')
        .map((line) => (line.trim() === '' ? prefix : `${prefix} ${line}`))
      result.push(linesWithPrefix.join('\n'))
    })
    return result.join(`\n${prefix}\n`)
  },

  addNodeView() {
    return ReactNodeViewRenderer(() => (
      <NodeViewWrapper as="div" className="bg-element my-3 rounded-lg p-3 not-italic text-inherit">
        <NodeViewContent />
      </NodeViewWrapper>
    ))
  },
})
